import { GripVertical } from 'lucide-react'

import { appointmentStatusTone, getAppointmentBlockStyle } from '@/lib/calendar/dayCalendar'
import { cn } from '@/lib/utils'

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  checked_in: 'Checked in',
  in_chair: 'In chair',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No show',
}

/**
 * @param {string} iso
 */
function formatClock(iso) {
  const date = new Date(iso)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
function primaryServiceLabel(appointment) {
  const first = appointment.services?.[0]
  return first?.service?.name ?? 'Appointment'
}

/**
 * @param {string} status
 */
function isDraggableStatus(status) {
  return !['cancelled', 'no_show', 'completed'].includes(status)
}

/**
 * @param {{
 *   appointment: import('@/types/api').Appointment
 *   stylistColour?: string | null
 *   dayStartMinutes: number
 *   pxPerMinute: number
 *   lane?: { column: number, totalColumns: number }
 *   onDragStart: (appointment: import('@/types/api').Appointment) => void
 *   onSelect?: (appointment: import('@/types/api').Appointment) => void
 *   rescheduleEnabled?: boolean
 *   rescheduling?: boolean
 *   compact?: boolean
 * }} props
 */
export function CalendarAppointmentBlock({
  appointment,
  stylistColour,
  dayStartMinutes,
  pxPerMinute,
  lane,
  onDragStart,
  onSelect,
  rescheduleEnabled = false,
  rescheduling = false,
  compact = false,
}) {
  const tone = appointmentStatusTone(appointment.status)
  const style = getAppointmentBlockStyle(
    appointment.starts_at,
    appointment.ends_at,
    dayStartMinutes,
    pxPerMinute,
  )
  const draggable = isDraggableStatus(appointment.status)
  const accent = stylistColour ?? 'hsl(var(--primary))'
  const isShortBlock = style.height < 44
  const useCompact = compact || isShortBlock || (lane?.totalColumns ?? 1) > 1
  const laneWidth = lane ? 100 / lane.totalColumns : 100
  const laneLeft = lane ? (lane.column / lane.totalColumns) * 100 : 0

  return (
    <div
      className={cn(
        'absolute z-10 overflow-hidden rounded-lg border text-left shadow-sm transition-shadow',
        useCompact ? 'px-1 py-0.5' : 'px-1.5 py-1',
        tone === 'active' && 'border-primary/35 bg-primary/12 text-primary',
        tone === 'confirmed' && 'border-sky-500/25 bg-sky-50/90 text-sky-950',
        tone === 'scheduled' && 'border-border/70 bg-card/95 text-foreground',
        tone === 'muted' && 'border-muted/80 bg-muted/50 text-muted-foreground opacity-75',
        rescheduling && 'pointer-events-none opacity-60',
        onSelect && 'cursor-pointer hover:shadow-md hover:ring-1 hover:ring-primary/20',
      )}
      style={{
        top: style.top,
        height: style.height,
        left: `calc(${laneLeft}% + 2px)`,
        width: `calc(${laneWidth}% - 4px)`,
        borderLeftWidth: 3,
        borderLeftColor: accent,
      }}
      title={`${appointment.customer?.name ?? 'Guest'} · ${formatClock(appointment.starts_at)}–${formatClock(appointment.ends_at)}`}
    >
      <div className="flex h-full items-start gap-0.5">
        {rescheduleEnabled && draggable ? (
          <span
            draggable={!rescheduling}
            onDragStart={(event) => {
              event.stopPropagation()
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('text/plain', String(appointment.id))
              onDragStart(appointment)
            }}
            className="mt-0.5 shrink-0 cursor-grab touch-none active:cursor-grabbing"
            aria-label={`Drag to reschedule ${appointment.customer?.name ?? 'booking'}`}
          >
            <GripVertical className="h-3 w-3 opacity-50" />
          </span>
        ) : null}

        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => onSelect?.(appointment)}
        >
          <p className={cn('truncate font-semibold leading-tight', useCompact ? 'text-[10px]' : 'text-[11px]')}>
            {appointment.customer?.name ?? 'Guest'}
          </p>
          {!useCompact ? (
            <p className="truncate text-[10px] opacity-80">{primaryServiceLabel(appointment)}</p>
          ) : null}
          <p className={cn('tabular-nums opacity-75', useCompact ? 'text-[9px]' : 'text-[10px]')}>
            {formatClock(appointment.starts_at)}
            {!useCompact ? ` – ${formatClock(appointment.ends_at)}` : ''}
          </p>
          {!useCompact && appointment.source === 'online' ? (
            <span className="mt-0.5 inline-block rounded bg-sky-100 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-sky-800">
              Online{appointment.online_pending_approval ? ' · pending' : ''}
            </span>
          ) : null}
          {!useCompact && appointment.status !== 'scheduled' && appointment.status !== 'confirmed' ? (
            <span className="mt-0.5 inline-block rounded bg-background/80 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide">
              {STATUS_LABELS[appointment.status] ?? appointment.status}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  )
}
